/**
 * Настенная сантехника: умывальник, полотенцесушитель, точки водоснабжения — на плане и на развёртке.
 *
 * Рисуется в локальных координатах — начало в центре объекта, ось X вдоль стены.
 * На плане объект виден сверху и занимает «ширина × глубина от стены», на развёртке —
 * спереди, «ширина × высота»: полотенцесушитель высотой 800 не должен «ложиться»
 * на 800 мм в комнату. Начинка — символ раздела, здесь выделение и зона захвата.
 * Квадратной подложки нет — как у мебели, символ залит белым только по своему контуру.
 */
import type { PointerEvent } from "react";
import { itemDimensions, type Fixture } from "@/shared/plan";
import type { CatalogItem } from "@/shared/catalog";
import type { SymbolFacing } from "@/shared/ui";
import { CatalogSymbol, symbolRenderSize } from "../../CatalogSymbol";
import { planInk } from "../../planInk";

/** Цвет и габариты, если типа в каталоге больше нет — объект всё равно виден и удаляем. */
const MISSING = { width: 80, height: 80, depth: 80, color: "#9aa3b2" };

/** Второе измерение символа: на плане — глубина от стены, на развёртке — высота. */
export const wallItemPlanSize = (
	item: { width: number; height: number; depth: number },
	facing: SymbolFacing,
) => (facing === "plan" ? item.depth : item.height);

interface Props {
	fixture: Fixture;
	/** Тип из каталога; null — тип удалён из каталога */
	item: CatalogItem | null;
	selected: boolean;
	/** Точка водоснабжения без трубы — обводим предупреждающим пунктиром */
	unconnected?: boolean;
	/** Размер миллиметра в пикселях — линии постоянной толщины на экране */
	mmPerPx: number;
	interactive?: boolean;
	/** Увеличение символа: на плане он мельче стены и нуждается в укрупнении */
	scale?: number;
	/** Минимальный размер символа на экране, пикселей */
	minPx?: number;
	/** Сверху на плане или спереди на развёртке */
	facing?: SymbolFacing;
	onPointerDown?: (e: PointerEvent<SVGGElement>) => void;
}

export const PlumbingItemShape = ({
	fixture,
	item,
	selected,
	unconnected = false,
	mmPerPx,
	interactive = true,
	scale = 1,
	minPx = 0,
	facing = "plan",
	onPointerDown,
}: Props) => {
	const size = item ? itemDimensions(fixture, item) : MISSING;
	const { width: w, height: h } = symbolRenderSize(
		size.width,
		wallItemPlanSize(size, facing),
		mmPerPx,
		scale,
		minPx,
	);
	const px = (n: number) => n * mmPerPx;
	// Подложки нет — светлый предмет (белый кондиционер) затемняем, как мебель, иначе
	// его линии пропадут на белой стене развёртки
	const color = selected ? "#2f6feb" : planInk(item?.color ?? MISSING.color);

	return (
		<g
			onPointerDown={onPointerDown}
			style={{
				cursor: interactive ? "grab" : "default",
				pointerEvents: interactive ? "auto" : "none",
			}}
		>
			{selected && (
				<rect
					x={-w / 2 - px(1)}
					y={-h / 2 - px(1)}
					width={w + px(2)}
					height={h + px(2)}
					rx={px(2)}
					fill="none"
					stroke="#2f6feb"
					strokeWidth={px(1)}
				/>
			)}

			<CatalogSymbol
				item={item}
				width={w}
				height={h}
				color={color}
				mmPerPx={mmPerPx}
				facing={facing}
			/>

			{/* Точка без трубы: пунктирное кольцо, чтобы на плане было видно, куда ещё не подвели воду */}
			{unconnected && !selected && (
				<rect
					x={-w / 2 - px(2.5)}
					y={-h / 2 - px(2.5)}
					width={w + px(5)}
					height={h + px(5)}
					rx={px(2)}
					fill="none"
					stroke="#d97706"
					strokeWidth={px(1)}
					strokeDasharray={`${px(2.5)} ${px(2)}`}
				/>
			)}

			{/* Прозрачная зона захвата: сам значок мелкий, попасть по нему трудно */}
			<rect
				data-role="fixture"
				data-id={fixture.id}
				x={-w / 2 - px(6)}
				y={-h / 2 - px(6)}
				width={w + px(12)}
				height={h + px(12)}
				fill="transparent"
			/>
		</g>
	);
};
